<template>
  <AdminAssetTable
    :config="digitalEntitiesConfig"
    :items="digitalEntitiesData"
    :users="users"
    :api-service="digitalEntitiesApiService"
    @refresh="loadDigitalEntities"
    @created="handleDigitalEntityCreated"
    @updated="handleDigitalEntityUpdated"
    @deleted="handleDigitalEntityDeleted"
  />
</template>

<script setup lang="ts">
import { digitalEntities } from '@/api/digitalEntities';
import type { Entity } from '@/api/types/core/common';
import type { User } from '@/api/types/core/users';
import type { DigitalEntity } from '@/api/types/entities/digitalEntity/types';
import { fetchAllUsers } from '@/api/users';
import AdminAssetTable from '@/components/admin/AdminAssetTable.vue';
import { ADMIN_ASSET_CONFIGS } from '@/configs/adminAssetConfigs';
import { onMounted, ref } from 'vue';

// Configuration
const digitalEntitiesConfig = ADMIN_ASSET_CONFIGS.digitalEntities;

// État
const digitalEntitiesData = ref<DigitalEntity[]>([]);
const users = ref<User[]>([]);
const loading = ref(false);

// Service API adapté au format attendu par AdminAssetTable
const digitalEntitiesApiService = {
  async getAll(): Promise<DigitalEntity[]> {
    return await digitalEntities.getAllAdminWithDeleted();
  },
  
  async create(data: Record<string, unknown>): Promise<DigitalEntity> {
    return await digitalEntities.createDigitalEntity(data as any);
  },
  
  async update(uuid: string, data: Record<string, unknown>): Promise<DigitalEntity> {
    return await digitalEntities.updateDigitalEntity(uuid, data as any);
  },
  
  async delete(uuid: string): Promise<void> {
    return await digitalEntities.deleteDigitalEntity(uuid);
  },
  async hardDelete(uuid: string): Promise<void> {
    return await digitalEntities.hardDeleteDigitalEntity(uuid);
  },
  async restore(uuid: string): Promise<void> {
    return await digitalEntities.restoreDigitalEntity(uuid);
  }
};

// Méthodes
const loadDigitalEntities = async (): Promise<void> => {
  loading.value = true;
  try {
    digitalEntitiesData.value = await digitalEntitiesApiService.getAll();
  } catch (error) {
    console.error('Erreur lors du chargement des entités numériques:', error);
  } finally {
    loading.value = false;
  }
};

const loadUsers = async (): Promise<void> => {
  try {
    users.value = await fetchAllUsers();
  } catch (error) {
    console.error('Erreur lors du chargement des utilisateurs:', error);
  }
};

// Gestionnaires d'événements
const handleDigitalEntityCreated = (digitalEntity: Entity): void => {
  digitalEntitiesData.value.push(digitalEntity as DigitalEntity);
};

const handleDigitalEntityUpdated = (updatedDigitalEntity: Entity): void => {
  const index = digitalEntitiesData.value.findIndex((a: DigitalEntity): boolean => a.uuid === updatedDigitalEntity.uuid);
  if (index !== -1) {
    digitalEntitiesData.value[index] = updatedDigitalEntity as DigitalEntity;
  }
};

const handleDigitalEntityDeleted = (uuid: string): void => {
  digitalEntitiesData.value = digitalEntitiesData.value.filter((a: DigitalEntity): boolean => a.uuid !== uuid);
};

// Lifecycle
onMounted(async (): Promise<void> => {
  await Promise.all([
    loadDigitalEntities(),
    loadUsers()
  ]);
});
</script> 